import { ENDPOINT } from '~/constants';

import { type KyRequestOptions } from '@vestigas/data';

import { HTTPError, vestigasApi } from '~/services/kyClient';
import ToastService from '~/services/toast.service';

import { i18n, log } from '~/utils';

import { type DeliveryNoteAttachmentAdminPayload } from './types';
import {
  zodDeliveryNoteAttachmentAdminEndpointSchema,
  zodDeliveryNoteAttachmentAdminSearchParamsSchema,
} from './validations';

/**
 * Fetches delivery note attachment data from the API.
 * @param deliveryNoteChainId - The ID of the delivery note chain.
 * @param searchParams - The search params for the attachment.
 * @param options - The options for the request.
 * @returns The attachment data as a blob, JSON object, or string based on content type.
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Asset/operation/get_delivery_note_attachment_vadmin_vadmin_delivery_note__id__attachment_get
 */
export const fetchDeliveryNoteAttachmentAdmin = async (
  deliveryNoteChainId: DeliveryNoteAttachmentAdminPayload['deliveryNoteChainId'],
  searchParams: Pick<DeliveryNoteAttachmentAdminPayload, 'digest' | 'name'>,
  options: KyRequestOptions,
  showErrorToast = true,
): Promise<Blob | Record<string, unknown> | string> => {
  try {
    const response = await vestigasApi.get(
      ENDPOINT.DELIVERY.GET_ATTACHMENT_ADMIN(deliveryNoteChainId),
      {
        searchParams,
        validate: {
          searchParams: zodDeliveryNoteAttachmentAdminSearchParamsSchema,
          pathSegments: zodDeliveryNoteAttachmentAdminEndpointSchema,
        },
        ...options,
      },
    );

    // Get content type from response headers to determine how to parse the response
    const contentType = response.headers.get('content-type')?.toLowerCase();

    // Process response based on content type
    if (contentType?.includes('application/json')) {
      return await response.json();
    }

    if (
      contentType?.includes('application/pdf') ||
      contentType?.includes('application/octet-stream') ||
      contentType?.includes('image/')
    ) {
      return await response.blob();
    }

    // Default to text for other content types
    return await response.text();
  } catch (error) {
    if (error instanceof DOMException && error.name === 'AbortError') {
      // Re-throw AbortError without logging as it's expected when requests are cancelled
      throw error;
    }

    log.error({
      message: 'Error fetching Delivery Note Attachment Admin',
      error,
    });

    let errorMessage = '';

    if (error instanceof HTTPError) {
      switch (error.response.status) {
        case 404: {
          errorMessage = i18n.t(
            ($) => $.deliveryNote.getAttachmentAdmin.error404,
            {
              ns: 'data',
            },
          );
          break;
        }

        case 422: {
          errorMessage = i18n.t(
            ($) => $.deliveryNote.getAttachmentAdmin.error422,
            {
              ns: 'data',
            },
          );
          break;
        }

        default: {
          errorMessage = i18n.t(
            ($) => $.deliveryNote.getAttachmentAdmin.error.generic,
            { ns: 'data' },
          );
          break;
        }
      }
    }

    if (showErrorToast && errorMessage) {
      ToastService.error(errorMessage);
    }

    // Re-throw the error to propagate it to the calling function.
    throw error;
  }
};
